/**
 * FDV — superficies promocionales pasivas. Doc 16 §7 paso 2.
 *
 * Custom elements que se suscriben a `fdv:offer:state` (del cerebro
 * `fdv-offer-state.js`) y muestran promos que YA existen (descuentos automáticos,
 * umbrales de regalo). NO capturan email, NO mutan el carrito, NO son
 * interruptivos. Si el cerebro no publica nada → cada elemento se queda con su
 * contenido de respaldo (server-rendered) y es inerte.
 *
 * Se carga global desde snippets/fdv-gift-engine.liquid (junto a fdv-offer-state).
 * Los custom elements no hacen nada hasta que se instancian.
 *
 *   <fdv-promo-announcement>  → slide dinámico de la barra de anuncio (patch #27)
 *   <fdv-promo-pdp>           → tira bajo el precio en la PDP
 *
 * Contador regresivo (2026-09-10): con el atributo `data-countdown` y una oferta
 * con fecha de término (`end_at` del metaobjeto → `offer.endsAt`, epoch s), la
 * superficie añade una cuenta regresiva viva ("· termina en 3 h 24 min"). Al
 * llegar a cero —o si el `fdv:offer:state` ya no reporta la oferta— cae al texto
 * de respaldo server-rendered. Reutilizable para toda venta con fecha.
 */

const OFFER_EVENT = 'fdv:offer:state';

function pickOffer(key, opts) {
  const FDV = window.FDV || {};
  if (key && typeof FDV.getOffer === 'function') return FDV.getOffer(key);
  // Sin `excludeApplied`: la superficie REFLEJA el estado (progreso →
  // celebración al desbloquear), no vende "activá tu promo". `bestOffer()` ya
  // prioriza las no-desbloqueadas, y si la única activa está desbloqueada
  // muestra su `unlocked_headline`.
  if (typeof FDV.bestOffer === 'function') return FDV.bestOffer(opts);
  return null;
}

/* ------------------------------------------------------- cuenta regresiva -- */

/** ms restantes → español compacto: "3 h 24 min" · "24 min 05 s" · "45 s". */
function formatRemaining(ms) {
  const total = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const pad = (n) => String(n).padStart(2, '0');
  if (h > 0) return `${h} h ${pad(m)} min`;
  if (m > 0) return `${m} min ${pad(s)} s`;
  return `${s} s`;
}

/** Token opcional en el copy para fijar dónde va el reloj. */
const CD_TOKEN = /\{\{\s*(?:cuenta_regresiva|termina)\s*\}\}/;

/**
 * Escribe `message` en `textEl`. Con `opts.countdown` y `opts.endsAtMs` futuro
 * inserta una cuenta regresiva viva: en el token {{cuenta_regresiva}} si el copy
 * lo trae, o añadida al final como "· <label> <timer>". Al llegar a cero llama
 * `opts.onExpire()` y detiene el intervalo.
 *
 * El caller ya descartó los casos "sin mensaje" y "contador ya expirado" — aquí
 * solo se pinta. Devuelve una función de limpieza (parar el intervalo).
 */
function paintMessage(textEl, message, opts = {}) {
  const { countdown = false, endsAtMs = null, label = 'termina en', onExpire } = opts;
  const msg = String(message || '');

  if (countdown && endsAtMs && endsAtMs > Date.now()) {
    const timer = document.createElement('span');
    timer.className = 'fdv-promo-countdown';

    textEl.textContent = '';
    if (CD_TOKEN.test(msg)) {
      const [before, after] = msg.split(CD_TOKEN);
      if (before) textEl.append(document.createTextNode(before));
      textEl.append(timer);
      if (after) textEl.append(document.createTextNode(after));
    } else {
      textEl.append(document.createTextNode(`${msg} · ${label} `));
      textEl.append(timer);
    }

    let iv = 0;
    const tick = () => {
      const left = endsAtMs - Date.now();
      if (left <= 0) {
        clearInterval(iv);
        if (typeof onExpire === 'function') onExpire();
        return;
      }
      timer.textContent = formatRemaining(left);
    };
    tick();
    iv = setInterval(tick, 1000);
    return () => clearInterval(iv);
  }

  // Sin contador: quita el token si venía en el copy y escribe plano.
  textEl.textContent = msg
    .replace(CD_TOKEN, '')
    .replace(/\s{2,}/g, ' ')
    .replace(/\s+·\s*$/, '')
    .trim();
  return () => {};
}

/** ¿La oferta trae una fecha de término ya pasada? (contador expirado) */
function countdownExpired(el, offer) {
  if (!el.hasAttribute('data-countdown') || !offer || !offer.endsAt) return false;
  return offer.endsAt * 1000 <= Date.now();
}

/* ----------------------------------------------- <fdv-promo-announcement> -- */
/**
 * Envuelve el texto de respaldo de un slide de la barra de anuncio. Cuando hay
 * una oferta relevante, sustituye el texto (y opcionalmente el enlace del slide)
 * por su mensaje dinámico. Sin oferta → texto de respaldo intacto.
 *
 * data-offer-key       : handle de un `fdv_gift_offer` a seguir (vacío = bestOffer)
 * data-countdown       : añade una cuenta regresiva hasta `offer.endsAt`
 * data-countdown-label : etiqueta antes del reloj (default "termina en")
 */
class FdvPromoAnnouncement extends HTMLElement {
  connectedCallback() {
    // El <p> del slide de Horizon es el hermano/ancestro; buscamos el más cercano.
    this._textEl = this.querySelector('[data-fdv-promo-text]') || this.closest('.announcement-bar__slide')?.querySelector('.announcement-bar__text');
    this._linkEl = this.closest('.announcement-bar__slide')?.querySelector('.announcement-bar__link');
    this._fallbackText = this._textEl ? this._textEl.innerHTML : '';
    this._fallbackHref = this._linkEl ? this._linkEl.getAttribute('href') : null;

    this._onState = () => this.apply();
    document.addEventListener(OFFER_EVENT, this._onState);
    this.apply();
  }

  disconnectedCallback() {
    document.removeEventListener(OFFER_EVENT, this._onState);
    this._cleanup?.();
  }

  _showFallback() {
    this._cleanup?.();
    this._cleanup = null;
    if (!this._textEl) return;
    this._textEl.innerHTML = this._fallbackText;
    if (this._linkEl && this._fallbackHref != null) this._linkEl.setAttribute('href', this._fallbackHref);
  }

  apply() {
    if (!this._textEl) return;
    const offer = pickOffer(this.dataset.offerKey);
    const msg = offer && offer.message ? String(offer.message).trim() : '';

    if (!msg || countdownExpired(this, offer)) {
      this._showFallback();
      return;
    }

    this._cleanup?.();
    this._cleanup = paintMessage(this._textEl, msg, {
      countdown: this.hasAttribute('data-countdown'),
      endsAtMs: offer.endsAt ? offer.endsAt * 1000 : null,
      label: this.getAttribute('data-countdown-label') || 'termina en',
      onExpire: () => this._showFallback(),
    });

    if (this._linkEl) {
      const href = offer.surface?.ctaUrl || this._fallbackHref;
      if (href) {
        this._linkEl.setAttribute('href', href);
        this._linkEl.hidden = false;
      }
      const label = this._linkEl.querySelector('.visually-hidden');
      if (label) label.textContent = this._textEl.textContent;
    }
  }
}

if (!customElements.get('fdv-promo-announcement')) {
  customElements.define('fdv-promo-announcement', FdvPromoAnnouncement);
}

/* ------------------------------------------------------- <fdv-promo-pdp> -- */
/**
 * Tira compacta bajo el precio de la PDP — y, con `data-prefer-body`, también
 * el banner de promo del carrito (snippets/fdv-cart-promo.liquid, hermano sin
 * ese atributo). Sin oferta → texto de respaldo del bloque
 * (`data-fdv-promo-text`). Si el respaldo también está vacío → el elemento
 * queda `hidden`.
 *
 * Copy fijado 2026-09-16 (campaña Festín de Venus), 3 textos distintos a
 * propósito, cada uno con su rol — NO deben unificarse:
 *   - Barra de anuncio / carrito (sin calificar): `promo_headline`, corto.
 *   - Tira PDP (sin calificar):                   `promo_body`, explicativo.
 *   - Cualquier superficie (calificada):           `unlocked_headline`, celebración
 *                                                   — compartido a propósito.
 *
 * data-offer-key       : handle de un `fdv_gift_offer` concreto (vacío = bestOffer)
 * data-prefer-body     : mientras la oferta NO ha calificado, usa el `promo_body`
 *                        (más largo/explicativo) en vez del `message` genérico —
 *                        evita repetir literal el `promo_headline` de la barra de
 *                        anuncio en la misma pantalla del PDP. Sin este atributo
 *                        (uso en el carrito) usa siempre `offer.message`, igual
 *                        que la barra. Calificada, siempre cae a `offer.message`
 *                        (= `unlocked_headline`, compartido a propósito).
 * data-countdown       : añade una cuenta regresiva hasta `offer.endsAt`
 * data-countdown-label : etiqueta antes del reloj (default "termina en")
 */
class FdvPromoPdp extends HTMLElement {
  connectedCallback() {
    this._textEl = this.querySelector('[data-fdv-promo-text]');
    this._ctaEl = this.querySelector('[data-fdv-promo-cta]');
    this._fallbackText = this._textEl ? this._textEl.textContent.trim() : '';

    this._onState = () => this.apply();
    document.addEventListener(OFFER_EVENT, this._onState);

    // Recuperación tras un morph externo (el carrito, doc 15 §5.4): Horizon puede
    // re-escribir este elemento a su estado server-rendered inicial (hidden, hijos
    // originales) SIN volver a correr connectedCallback. El observer detecta el
    // reset y re-aplica; también re-consulta _textEl/_ctaEl porque el morph puede
    // reemplazar esos nodos hijos por otros nuevos (una referencia vieja quedaría
    // huérfana y las escrituras dejarían de verse). `_writing` evita que nuestras
    // propias escrituras (p. ej. `this.hidden = false`) re-disparen el observer.
    this._writing = false;
    this._mo = new MutationObserver(() => {
      if (this._writing) return;
      this._textEl = this.querySelector('[data-fdv-promo-text]');
      this._ctaEl = this.querySelector('[data-fdv-promo-cta]');
      this.apply();
    });
    this._mo.observe(this, { attributes: true, attributeFilter: ['hidden'], childList: true });

    this.apply();
  }

  disconnectedCallback() {
    document.removeEventListener(OFFER_EVENT, this._onState);
    this._mo?.disconnect();
    this._cleanup?.();
  }

  _showFallback() {
    this._cleanup?.();
    this._cleanup = null;
    if (!this._textEl) return;
    this._writing = true;
    this._textEl.textContent = this._fallbackText;
    if (this._ctaEl) this._ctaEl.hidden = true;
    // Solo escribir si cambia: `el.hidden = true` sobre un elemento ya oculto
    // igual encola un registro del MutationObserver (setAttribute siempre
    // registra) → apply() → _showFallback() → bucle infinito de microtareas
    // que congela la página (oferta fijada expirada + respaldo vacío).
    const hide = this._fallbackText === '';
    if (this.hidden !== hide) this.hidden = hide;
    // Los registros del observer llegan async, DESPUÉS de `_writing = false`:
    // el flag por sí solo no protege. Se descartan los propios aquí.
    this._mo?.takeRecords();
    this._writing = false;
  }

  apply() {
    if (!this._textEl) return;
    const offer = pickOffer(this.dataset.offerKey);
    const bodyMsg =
      offer && !offer.qualifies && this.hasAttribute('data-prefer-body') ? String(offer.surface?.body || '').trim() : '';
    const msg = bodyMsg || (offer && offer.message ? String(offer.message).trim() : '');

    if (!msg || countdownExpired(this, offer)) {
      this._showFallback();
      return;
    }

    this._writing = true;
    this._cleanup?.();
    this._cleanup = paintMessage(this._textEl, msg, {
      countdown: this.hasAttribute('data-countdown'),
      endsAtMs: offer.endsAt ? offer.endsAt * 1000 : null,
      label: this.getAttribute('data-countdown-label') || 'termina en',
      onExpire: () => this._showFallback(),
    });
    if (this.hidden) this.hidden = false;

    const url = offer.surface?.ctaUrl || '';
    const label = offer.surface?.ctaLabel || '';
    if (this._ctaEl && url && label) {
      this._ctaEl.textContent = label;
      this._ctaEl.setAttribute('href', url);
      this._ctaEl.hidden = false;
    } else if (this._ctaEl) {
      this._ctaEl.hidden = true;
    }
    this._mo?.takeRecords();
    this._writing = false;
  }
}

if (!customElements.get('fdv-promo-pdp')) {
  customElements.define('fdv-promo-pdp', FdvPromoPdp);
}

export { formatRemaining, paintMessage };
